import PropTypes from 'prop-types'
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs'
import { useState, useMemo, useCallback } from 'react'
import DraggableStudent from './DraggableStudent'
import StudentCard from '../Pairings/StudentCard'
import { ReactComponent as ClapIcon } from '../../../assets/images/common/icons/clap-icon.svg'
import { ReactComponent as SortIcon } from '../../../assets/images/common/icons/sort-icon-2.svg'
import { UncontrolledPopover } from 'reactstrap'
import SearchInput from '../../common/SearchInput'
import { DragOverlay } from '@dnd-kit/core'

import styles from './ManagePairings.module.scss'
import classNames from 'classnames'

const PairingsStudents = ({ event, draggingId, draggedIds }) => {
  const [tabIndex, setTabIndex] = useState(0)
  const [searchAttendingValue, setSearchAttendingValue] = useState('')
  const [searchNotAttendingValue, setSearchNotAttendingValue] = useState('')
  const [searchWaitlistValue, setSearchWaitlistValue] = useState('')
  const [attendingStudentsFiltered, setAttendingStudentsFiltered] = useState([])
  const [noResponseStudentsFiltered, setNoResponseStudentsFiltered] = useState([])
  const [notAttendingStudentsFiltered, setNotAttendingStudentsFiltered] = useState([])
  const [waitlistedStudentsFiltered, setWaitlistedStudentsFiltered] = useState([])
  const [sortKeys, setSortKeys] = useState(['level'])

  const eventStudents = useMemo(() => (
    event.students ? [...event.students].filter((s) => !draggedIds.includes(s.id) ) : []
  ), [event.students, draggedIds])

  const sortFunction = useCallback((a, b) => {
    for (const key of sortKeys) {
      if (a[key] < b[key]) {
        return -1
      }
      if (a[key] > b[key]) {
        return 1
      }
    }
    return 0
  }, [sortKeys])

  const handleSortKeyChanged = (key) => {
    setSortKeys(prevKeys => prevKeys.includes(key)
      ? prevKeys.filter(value => value !== key)
      : [...prevKeys, key]
    )
  }

  const rosterStudents = useMemo(() => {
    const students = [...eventStudents]
      .filter((s) => s.status === 'confirmed' || s.status === 'created' )
      .sort(sortFunction)
    setAttendingStudentsFiltered(students.filter((std) =>
      std.status === 'confirmed' && (
        std.firstName.toLowerCase().includes(searchAttendingValue.toLowerCase()) ||
        std.lastName.toLowerCase().includes(searchAttendingValue.toLowerCase()))
    ))

    setNoResponseStudentsFiltered(students.filter((std) =>
      std.status === 'created' && (
        std.firstName.toLowerCase().includes(searchAttendingValue.toLowerCase()) ||
        std.lastName.toLowerCase().includes(searchAttendingValue.toLowerCase()))
    ))
    return students
  }, [eventStudents, searchAttendingValue, sortFunction])

  const notAttendingStudents = useMemo(() => {
    const students = [...eventStudents]
      .filter((s) => s.status === 'declined')
      .sort(sortFunction)
    setNotAttendingStudentsFiltered(students.filter((std) =>
      std.firstName.toLowerCase().includes(searchNotAttendingValue.toLowerCase()) ||
      std.lastName.toLowerCase().includes(searchNotAttendingValue.toLowerCase())
    ))
    return students
  }, [eventStudents, searchNotAttendingValue, sortFunction])

  const waitlistedStudents = useMemo(() => {
    const students = [...eventStudents]
      .filter((s) => s.status === 'waitlisted')
      .sort(sortFunction)
    setWaitlistedStudentsFiltered(students.filter((std) =>
      std.firstName.toLowerCase().includes(searchWaitlistValue.toLowerCase()) ||
      std.lastName.toLowerCase().includes(searchWaitlistValue.toLowerCase())
    ))
    return students
  }, [eventStudents, searchWaitlistValue, sortFunction])

  const handleSearchChange = (e) => {
    const value = e.target.value

    switch (tabIndex) {
      case 0:
        setSearchAttendingValue(value)
        break
      case 1:
        setSearchWaitlistValue(value)
        break
      case 2:
        setSearchNotAttendingValue(value)
        break
      default:
        null
    }
  }

  const handleTabChange = (index) => {
    setTabIndex(index)
    if (index === 1) {
      setSortKeys(['waitlistOrder'])
    }
  }

  return (
    <Tabs selectedIndex={tabIndex} onSelect={handleTabChange} className='h-100'>
      <TabList className={classNames('d-flex justify-content-between px-3 ', styles.tabList)} >
        <Tab className={`${styles.tab} react-tabs__tab`} selectedClassName={styles.activeTab}>Roster ({rosterStudents.length})</Tab>
        { event.hasWaitlistEnabled &&
          <Tab className={`${styles.tab} react-tabs__tab`} selectedClassName={styles.activeTab}>Waitlist ({waitlistedStudents.length})</Tab>
        }
        <Tab className={`${styles.tab} react-tabs__tab`} selectedClassName={styles.activeTab}>Not Attending ({notAttendingStudents.length})</Tab>
      </TabList>
      <div className='d-flex'>
        <div className={classNames(styles.studentsSearchBar, 'mt-2 mx-3')}>
          <SearchInput
            value={tabIndex === 0 ? searchAttendingValue : tabIndex === 1 ? searchWaitlistValue : searchNotAttendingValue}
            placeholder='Search for Students'
            onChange={handleSearchChange}
          />
        </div>
        <div className={styles.sortIconWrapper} id='sortButton'>
          <SortIcon/>
        </div>
        <UncontrolledPopover
          placement='bottom'
          target='sortButton'
          trigger='legacy'
        >
          <div className={classNames('d-flex flex-column', styles.sortOptionsMenu)}>
            <span className={styles.optionsHeader}>Sort Students by</span>
            {event.hasWaitlistEnabled && tabIndex === 1 &&
              <span aria-hidden='true' className={classNames(styles.sortOption, sortKeys.includes('waitlistOrder') && styles.activeOption)}
                onClick={() => handleSortKeyChanged('waitlistOrder')}>Waitlist Order
              </span>
            }
            <span aria-hidden='true' className={classNames(styles.sortOption, sortKeys.includes('lastName') && styles.activeOption)}
              onClick={() => handleSortKeyChanged('lastName')}>Last Name (A-Z)
            </span>
            <span aria-hidden='true' className={classNames(styles.sortOption, sortKeys.includes('age') && styles.activeOption)}
              onClick={() => handleSortKeyChanged('age')}>Age
            </span>
            <span aria-hidden='true' className={classNames(styles.sortOption, sortKeys.includes('level') && styles.activeOption)}
              onClick={() => handleSortKeyChanged('level')}>Skill Level
            </span>
            <span aria-hidden='true' className={classNames(styles.sortOption, sortKeys.includes('gender') && styles.activeOption)}
              onClick={() => handleSortKeyChanged('gender')}>Gender
            </span>
          </div>
        </UncontrolledPopover>
      </div>

      <TabPanel className={classNames('mx-3', styles.tabPanel)} selectedClassName={styles.activeTabPanel}>
        <div className={styles.studentsList}>
          <p className={styles.studentsStatus}>{`Attending (${attendingStudentsFiltered.length})`}</p>
          {attendingStudentsFiltered.length !== 0 || searchAttendingValue ?
            attendingStudentsFiltered.map((student, index) => (
              <div key={index} className='d-flex align-items-center w-100'>
                <DraggableStudent key={student.id} student={student} draggingId={draggingId} draggedFrom='communityStudents'/>
              </div>
            )) :
            <div className={classNames('d-flex align-items-center', styles.attendingStudentsPlaceholder)}>
              <ClapIcon className={styles.clapIcon}/>
              <p>All Students have been paired!</p>
            </div>
          }
          <p className={styles.studentsStatus}>{`No Response (${noResponseStudentsFiltered.length})`}</p>
          {noResponseStudentsFiltered.length !== 0 || searchAttendingValue ?
            noResponseStudentsFiltered.map((student, index) => (
              <div key={index} className='d-flex align-items-center w-100'>
                <DraggableStudent key={student.id} student={student} draggingId={draggingId} draggedFrom='communityStudents'/>
              </div>
            )) :
            <p className={styles.noResponseStudentsPlaceholder}>No students left in this category</p>
          }
          <DragOverlay>
            {draggingId !== -1 && <StudentCard dragging student={rosterStudents.find(s => s.id === draggingId)} />}
          </DragOverlay>
        </div>
      </TabPanel>
      <TabPanel className={classNames('mx-3', styles.tabPanel)} selectedClassName={styles.activeTabPanel}>
        <div className={styles.studentsList}>
          {waitlistedStudents.length ?
            waitlistedStudentsFiltered.map((student, index) => (
              <div key={index} className='d-flex align-items-center w-100'>
                <DraggableStudent key={student.id} student={student} draggingId={draggingId} draggedFrom='communityStudents'/>
              </div>
            )) :
            <div className={classNames('d-flex flex-column align-items-center', styles.studentsPlaceholder)}>
              <ClapIcon className={styles.clapIcon}/>
              <p className={styles.placeholderTitle}>All Golfers are Paired</p>
              <p className={styles.placeholderMessage}>Congratulations, coach! All your golfers have been paired, don’t forget to save and submit!</p>
            </div>
          }
          <DragOverlay>
            {draggingId !== -1 && <StudentCard dragging student={eventStudents.find(s => s.id === draggingId)}/>}
          </DragOverlay>
        </div>
      </TabPanel>
      <TabPanel className={classNames('mx-3', styles.tabPanel)} selectedClassName={styles.activeTabPanel}>
        <div className={styles.studentsList}>
          {notAttendingStudents.length ?
            notAttendingStudentsFiltered.map((student, index) => (
              <div key={index} className='d-flex align-items-center w-100'>
                <DraggableStudent key={student.id} student={student} draggingId={draggingId} draggedFrom='communityStudents'/>
              </div>
            )) :
            <div className={classNames('d-flex flex-column align-items-center', styles.studentsPlaceholder)}>
              <ClapIcon className={styles.clapIcon}/>
              <p className={styles.placeholderTitle}>All Golfers are Paired</p>
              <p className={styles.placeholderMessage}>Congratulations, coach! All your golfers have been paired, don’t forget to save and submit!</p>
            </div>
          }
          <DragOverlay>
            {draggingId !== -1 && <StudentCard dragging student={eventStudents.find(s => s.id === draggingId)}/>}
          </DragOverlay>
        </div>
      </TabPanel>
    </Tabs>
  )
}

PairingsStudents.propTypes = {
  event: PropTypes.object,
  draggingId: PropTypes.number,
  draggedIds: PropTypes.array,
}

export default PairingsStudents
